import axios from "axios";
import { recordApiError } from "./errorTracker";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "hw_access_token";

export const getStoredToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
export const setStoredToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* storage unavailable */ }
};
export const clearStoredToken = () => setStoredToken(null);

export const api = axios.create({
  baseURL: API,
  // Bearer-token auth: we deliberately do NOT send credentials, so the proxy's
  // `Access-Control-Allow-Origin: *` is acceptable to strict browsers (iOS Safari/Chrome).
  withCredentials: false,
});

// Attach Bearer token from localStorage on every request
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Sprint 1B-B · US 1.5 · staff-api permission-revalidation bridge
// ---------------------------------------------------------------------------
// A 403 from the staff `api` instance is a completed backend denial. The
// bridge below lets an `AuthProvider` register a handler that is invoked
// (at most once per denial cluster) when an eligible 403 is observed. The
// interceptor itself:
//   - preserves and re-rejects with the original error (never swallowed);
//   - never replays the original request (any method — GET/POST/PUT/PATCH/DELETE);
//   - excludes the bootstrap / auth / logout / MFA / permissions-me endpoints
//     from triggering revalidation to prevent recursion;
//   - deduplicates simultaneous eligible 403s into a single handler invocation;
//   - is installed exactly once at module load — safe against React StrictMode
//     double-mount because it lives outside the React tree;
//   - does not import React or the auth context, avoiding circular imports.
//
// URL classification is by path shape, not by human-readable `detail` text.
// The backend does not emit a machine-readable permission-denial code today;
// message-text classification would be brittle across versions.

const REVALIDATION_EXCLUDE_PATTERNS = [
  /\/permissions\/me(?:\?|$)/,          // recursion guard
  /\/auth\/me(?:\?|$)/,                 // session bootstrap
  /\/auth\/login(?:\?|$)/,              // login
  /\/auth\/logout(?:\?|$)/,             // logout
  /\/auth\/mfa\//,                      // any MFA verify/complete/reenroll
];

export function isRevalidationEligibleForUrl(url) {
  if (typeof url !== "string" || url.length === 0) return false;
  return !REVALIDATION_EXCLUDE_PATTERNS.some((re) => re.test(url));
}

let _permissionRevalidationHandler = null;
let _permissionRevalidationInflight = false;

/**
 * Register a permission-revalidation handler. Returns an unsubscribe
 * function. Only one handler is authoritative at a time; registering a
 * new handler replaces the previous one (last writer wins).
 */
export function registerPermissionRevalidationHandler(handler) {
  if (typeof handler !== "function") {
    throw new TypeError("handler must be a function");
  }
  _permissionRevalidationHandler = handler;
  return () => {
    if (_permissionRevalidationHandler === handler) {
      _permissionRevalidationHandler = null;
    }
  };
}

/** Test-only reset. Not part of the public API surface. */
export function __resetPermissionRevalidationBridgeForTests() {
  _permissionRevalidationHandler = null;
  _permissionRevalidationInflight = false;
}

// 403 interceptor is registered BEFORE the existing 401 interceptor so
// eligible denials always trigger revalidation, regardless of any later
// 401-handling side effects.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 403 && isRevalidationEligibleForUrl(url)) {
      const handler = _permissionRevalidationHandler;
      if (handler && !_permissionRevalidationInflight) {
        _permissionRevalidationInflight = true;
        // Fire-and-forget: never blocks or mutates the original rejection.
        Promise.resolve()
          .then(() => handler({ url, status: 403 }))
          .catch(() => { /* handler errors do not replace original 403 */ })
          .finally(() => { _permissionRevalidationInflight = false; });
      }
    }
    return Promise.reject(error);   // ORIGINAL rejection preserved
  }
);

// Auto-handle session loss: when any API call returns 401, force a clean re-auth.
// EXCEPT for endpoints that legitimately use 401 to signal "wrong confirmation
// password" (not "session expired") — those let the calling component show its
// own inline error and stay on the page.
const PASSWORD_CONFIRM_PATTERNS = [
  /\/auth\/mfa\/reenroll\/password$/,         // step-up before add device / regen codes
  /\/auth\/mfa\/devices\/[^/]+$/,             // DELETE device (requires password)
  /\/auth\/mfa\/reenroll\/verify$/,           // legacy single-secret rotate
];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    // Record server/network failures so HelpBeacon can pre-fill High
    // priority. 4xx client errors are excluded — those are usually
    // expected validation responses, not "something is broken".
    if (status === 0 || status >= 500 || (!error?.response && error?.code !== "ERR_CANCELED")) {
      recordApiError({ status: status || 0, url, message: error?.message });
    }
    const onLoginPage = typeof window !== "undefined" && window.location.pathname.startsWith("/login");
    const onPortal = typeof window !== "undefined" && window.location.pathname.startsWith("/portal");
    const isMeCheck = url.includes("/auth/me");
    const isPasswordConfirm = PASSWORD_CONFIRM_PATTERNS.some((re) => re.test(url));
    if (status === 401 && !onLoginPage && !onPortal && !isMeCheck && !isPasswordConfirm) {
      clearStoredToken();
      if (typeof window !== "undefined" && !window.__hw_redirecting) {
        window.__hw_redirecting = true;
        window.location.replace("/login?expired=1");
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Legacy compatibility re-exports. The canonical source of truth lives in
// @/lib/brandingConfig (see the BRAND constant). New code should import
// { BRAND } from "@/lib/brandingConfig" and use BRAND.logo / BRAND.mark.
// These string constants are kept identical to BRAND.logo / BRAND.mark by
// asserting via the branding verification script.
export { HARBORWELL_LOGO, HARBORWELL_MARK } from "./brandingConfig";

export const ROLE_LABELS = {
  admin: "System Administrator",
  technical_support: "Technical Support",
  executive_director: "Supervisor",
  care_navigator: "Care Navigator",
  field_specialist: "Field Specialist",
  volunteer: "Volunteer",
};

export const REFERRAL_CATEGORIES = [
  { value: "housing", label: "Housing" },
  { value: "food", label: "Food" },
  { value: "transportation", label: "Transportation" },
  { value: "healthcare", label: "Healthcare" },
  { value: "maternal_wellness", label: "Maternal & Family" },
  { value: "childcare", label: "Childcare" },
  { value: "employment", label: "Employment" },
  { value: "counseling", label: "Counseling" },
  { value: "domestic_violence_support", label: "Domestic Violence Support" },
];

// Resources extend referral categories with community-specific options
export const RESOURCE_CATEGORIES = [
  ...REFERRAL_CATEGORIES,
  { value: "caregiver_support", label: "Caregiver Support" },
  { value: "aging_population", label: "Aging Population" },
  { value: "education", label: "Education" },
  { value: "financial_assistance", label: "Financial Assistance" },
  { value: "clothing", label: "Clothing" },
  { value: "recovery", label: "Recovery" },
  { value: "emergency_assistance", label: "Emergency Assistance" },
  { value: "veteran_services", label: "Veteran Services" },
  { value: "disability_support", label: "Disability Support Services" },
  { value: "youth_services", label: "Youth Services" },
  { value: "legal_assistance", label: "Legal Assistance" },
];

export const INTERACTION_TYPES = [
  { value: "phone", label: "Phone Call" },
  { value: "text", label: "Text Message" },
  { value: "email", label: "Email" },
  { value: "in_person", label: "In Person" },
  { value: "home_visit", label: "Home Visit" },
  { value: "outreach_event", label: "Outreach Event" },
];

export const CARE_SEEKER_STATUS = [
  { value: "active", label: "Active" },
  { value: "follow_up", label: "Follow-up Needed" },
  { value: "inactive", label: "Inactive" },
  { value: "closed", label: "Closed" },
];

export const INTAKE_CHANNELS = [
  { value: "hotline_call", label: "Hotline call" },
  { value: "email", label: "Email" },
  { value: "text", label: "Text message" },
  { value: "in_person", label: "In person" },
  { value: "walk_in", label: "Walk-in" },
  { value: "website", label: "Website form" },
  { value: "partner_referral", label: "Partner referral" },
  { value: "social_media", label: "Social media" },
  { value: "other", label: "Other" },
];

export const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

export const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const formatRelative = (iso) => {
  if (!iso) return null;
  try {
    const now = new Date();
    const then = new Date(iso);
    const diffMs = now - then;
    if (isNaN(diffMs)) return null;
    const minutes = Math.round(diffMs / 60000);
    const hours = Math.round(minutes / 60);
    const days = Math.round(hours / 24);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} min ago`;
    if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
    if (days === 1) return "yesterday";
    if (days < 30) return `${days} days ago`;
    const months = Math.round(days / 30);
    if (months < 12) return `${months} mo${months === 1 ? "" : "s"} ago`;
    const years = Math.round(days / 365);
    return `${years} yr${years === 1 ? "" : "s"} ago`;
  } catch {
    return null;
  }
};

export const daysSince = (iso) => {
  if (!iso) return null;
  try {
    const ms = new Date() - new Date(iso);
    if (isNaN(ms)) return null;
    return Math.floor(ms / 86400000);
  } catch {
    return null;
  }
};

